import { useMemo, useState, useCallback } from "react";
import WorldMap from "../components/WorldMap";
import ProjectDrawer from "../components/ProjectDrawer";
import { allProjects, countries } from "../data/projects";

export default function Projects() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);
  const [focused, setFocused] = useState(null);

  const byCountry = useMemo(() => {
    const groups = {};
    for (const p of allProjects) {
      if (!groups[p.country]) groups[p.country] = [];
      groups[p.country].push(p);
    }
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, []);

  const open = useCallback((iso, pid = null) => {
    setActiveCountry(iso);
    setFocused(pid);
    setDrawerOpen(true);
  }, []);

  return (
    <div className="pt-28 pb-20">
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="overline">Project Atlas</div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]" style={{ color: "var(--primary)" }}>
          Every project, every country.
        </h1>
        <p className="mt-6 max-w-2xl text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
          A complete portfolio of {allProjects.length} renewable energy and decarbonization projects delivered over the last fifteen years. Click any country on the map, or browse the list below.
        </p>
      </section>

      <section className="mt-14 max-w-7xl mx-auto px-6 md:px-12">
        <WorldMap onSelectCountry={(iso) => open(iso)} />
      </section>

      <section data-testid="projects-country-list" className="mt-24 max-w-7xl mx-auto px-6 md:px-12 space-y-12">
        {byCountry.map(([iso, list]) => (
          <div key={iso} id={`country-${iso}`} data-testid={`country-group-${iso}`}>
            <div className="flex items-end justify-between gap-4 flex-wrap border-b pb-4" style={{ borderColor: "var(--border-soft)" }}>
              <h2 className="font-serif text-3xl md:text-4xl" style={{ color: "var(--primary)" }}>
                {countries[iso]?.name || iso}
              </h2>
              <button
                data-testid={`open-country-${iso}`}
                onClick={() => open(iso)}
                className="text-xs font-mono uppercase tracking-widest link-underline font-semibold"
                style={{ color: "var(--accent-strong)" }}
              >
                View as drawer · {list.length} projects
              </button>
            </div>
            <ul className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ backgroundColor: "var(--border-soft)" }}>
              {list.map((p) => (
                <li key={p.id} style={{ backgroundColor: "var(--bg)" }}>
                  <button
                    onClick={() => open(iso, p.id)}
                    data-testid={`country-project-${p.id}`}
                    className="text-left w-full p-5 transition-colors"
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--surface)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-serif text-lg" style={{ color: "var(--primary)" }}>{p.name}</h3>
                      {p.flagship && <span className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: "var(--accent-strong)" }}>★</span>}
                    </div>
                    <div className="mt-1 text-xs font-medium" style={{ color: "var(--text-secondary)" }}>{p.sector}</div>
                    <div className="mt-2 text-sm font-medium" style={{ color: "var(--text-primary)" }}>{p.capacity}</div>
                    <div className="mt-3 text-[11px] font-mono font-medium" style={{ color: "var(--text-secondary)" }}>{p.year} · {p.role}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <ProjectDrawer
        countryIso={activeCountry}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        focusedProjectId={focused}
      />
    </div>
  );
}
